import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Search } from 'lucide-react';
import type { ClassificationKind, CsiList, IsicList, LcList } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';

export interface CodeNode {
  code: string;
  level: number;
  parent_code: string | null;
  name_en: string;
  name_ar: string;
  child_count: number;
  is_active: boolean;
  extra?: string | null;
}

const ENDPOINT: Record<ClassificationKind, string> = { isic: '/api/classification/isic', csi: '/api/classification/csi', lc: '/api/classification/lc' };
const LEAF: Record<ClassificationKind, number> = { isic: 5, csi: 4, lc: 3 };

function normalise(kind: ClassificationKind, items: IsicList['items'] | CsiList['items'] | LcList['items']): CodeNode[] {
  return items.map((i) => {
    if (kind === 'csi') {
      const c = i as CsiList['items'][number];
      return { code: c.code, level: c.level, parent_code: c.parent_code, name_en: c.title_en, name_ar: c.title_ar, child_count: c.child_count, is_active: c.is_active };
    }
    if (kind === 'lc') {
      const l = i as LcList['items'][number];
      return { code: l.code, level: l.level, parent_code: l.parent_code, name_en: l.name_en, name_ar: l.name_ar, child_count: l.child_count, is_active: l.is_active, extra: l.price_ceiling !== null ? `${Math.round(l.price_ceiling * 100)}%` : null };
    }
    const x = i as IsicList['items'][number];
    return { code: x.code, level: x.level, parent_code: x.parent_code, name_en: x.name_en, name_ar: x.name_ar, child_count: x.child_count, is_active: x.is_active, extra: x.supervising_authority };
  });
}

export function useCodes(kind: ClassificationKind, query: { q?: string; level?: number; parent_code?: string }, enabled = true) {
  return useQuery({
    queryKey: ['classification', kind, query],
    queryFn: async () => normalise(kind, (await api.get<IsicList | CsiList | LcList>(`${ENDPOINT[kind]}${qs({ ...query, page_size: 200 })}`)).items),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Multi-pane browser (MRLCO's ISICBrowserModal pattern, shared by the three lists): panes open level by level;
 * a search box lists matches at any level. `onPick` receives leaf-level rows when `pickLeaf` is set.
 */
export function CodeBrowser({ kind, onPick, pickLeaf, selected, compact }: { kind: ClassificationKind; onPick?: (node: CodeNode) => void; pickLeaf?: boolean; selected?: Set<string>; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const [path, setPath] = useState<CodeNode[]>([]);
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => { const h = setTimeout(() => setTerm(q.trim()), 250); return () => clearTimeout(h); }, [q]);
  useEffect(() => setPath([]), [kind]);
  const parent = path[path.length - 1];
  const pane = useCodes(kind, parent ? { parent_code: parent.code } : { level: 1 }, !term);
  const search = useCodes(kind, { q: term }, Boolean(term));
  const leaf = LEAF[kind];
  const rows = term ? (search.data ?? []) : (pane.data ?? []);
  const label = (n: CodeNode) => pick(i18n.language, n.name_en, n.name_ar);
  return (
    <div className={cn('flex flex-col gap-2', compact ? 'max-h-96' : 'max-h-[32rem]')}>
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute start-2 top-2.5 text-neutral-400" />
        <input className="input ps-7" placeholder={t('beneficiaries.classification.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {!term && (
        <nav className="flex flex-wrap items-center gap-1 text-xs text-neutral-500">
          <button type="button" className="hover:underline" onClick={() => setPath([])}>{t(`beneficiaries.classification.kinds.${kind}`)}</button>
          {path.map((n, i) => <span key={n.code} className="flex items-center gap-1"><ChevronRight size={12} className="rtl:rotate-180" /><button type="button" className="hover:underline" dir="auto" onClick={() => setPath(path.slice(0, i + 1))}><code>{n.code}</code> {label(n)}</button></span>)}
        </nav>
      )}
      <ul className="min-h-0 flex-1 divide-y divide-neutral-100 overflow-y-auto rounded-lg border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
        {(pane.isLoading || search.isLoading) && <li className="p-3 text-sm text-neutral-500">{t('app.loading')}</li>}
        {rows.length === 0 && !pane.isLoading && !search.isLoading && <li className="p-3 text-sm text-neutral-500">{t('app.noResults')}</li>}
        {rows.map((n) => {
          const isLeaf = n.level >= leaf || n.child_count === 0;
          const pickable = Boolean(onPick) && (!pickLeaf || n.level >= leaf);
          return (
            <li key={n.code} className={cn('flex items-center gap-2 px-3 py-2 text-sm', selected?.has(n.code) && 'bg-brand-50 dark:bg-brand-700/20')}>
              <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-start" onClick={() => (isLeaf || term ? (pickable ? onPick?.(n) : undefined) : setPath([...path, n]))} disabled={isLeaf && !pickable && !term}>
                <code className="shrink-0 text-xs text-neutral-500">{n.code}</code>
                <span className="min-w-0 flex-1 truncate" dir="auto">{label(n)}</span>
                {term && <Badge>{t('beneficiaries.classification.level', { level: n.level })}</Badge>}
                {n.extra && <span className="hidden truncate text-xs text-neutral-400 md:inline" dir="auto">{n.extra}</span>}
                {!isLeaf && !term && <ChevronRight size={14} className="shrink-0 text-neutral-400 rtl:rotate-180" />}
              </button>
              {pickable && <button type="button" className="btn-secondary px-2 py-0.5 text-xs" onClick={() => onPick?.(n)}>{selected?.has(n.code) ? t('beneficiaries.classification.selected') : t('beneficiaries.classification.select')}</button>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
